/**
 * InfinitSales - WhatsApp Business Inbox Page
 * Padronizado com WhatsApp Pessoal
 * 
 * Diferenças mantidas:
 * - OAuth-based connection (no QR Code)
 * - Filters SSE events by source: 'wa_business'
 * - ConnectionModal específico para OAuth
 * - Token expired warning
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { channelOf, isOpaqueChannel, isOpaqueKey, openChatState, routeOf } from '../../utils/channels';
import { findChatByPhone } from '../../utils/phoneSearch';
import { WifiOff, X, Loader2, AlertCircle, UserCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSE, useSSEEvent, SSE_EVENTS, SSE_STATES } from '../../contexts/SSEContext';
import waBusinessService from '../../services/wa-business/waBusinessService';
import { isDelegated, getDelegation } from '../../utils/delegation';
import authService from '../../services/global/authService';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import subscriptionService from '../../services/global/subscriptionService';
import { getChatState, getChatStatesBatch, resumeAgent } from '../../services/agents/agentService';

// Components
import NavSidebar from '../../components/NavSidebar';
import NotificationBell from '../../components/notifications/NotificationBell';
import PromoModal from '../../components/PromoModal';
import AgentActivationModal from '../../components/AgentActivationModal';
import useAgentTrainingNotification from '../../hooks/useAgentTrainingNotification';
import CreateCompanyNotification from '../../components/notifications/CreateCompanyNotification';
import useCreateCompanyNotification from '../../hooks/useCreateCompanyNotification';
import ContactTransferredNotification from '../../components/notifications/ContactTransferredNotification';
import useContactTransferredNotifications from '../../hooks/useContactTransferredNotifications';
import SwipeToast from '../../components/notifications/SwipeToast';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
import ConnectionModal from '../../components/wa-business/ConnectionModal';
import EmptyInboxModal from '../../components/inbox/EmptyInboxModal';
import DelegatedDisconnectedState from '../../components/empresa/DelegatedDisconnectedState';
// Frentes C1, C2 e C4 (flags persistent_layout, inbox_store, chat_cache).
import { useInboxLayout } from '../../components/inbox/InboxLayout';
import { useChannelChats, setInboxScrollTopSilent } from '../../services/inbox/inboxStore';
import { getBootFlag } from '../../hooks/useFeatureFlags';
import {
  appendChatMessage, prefetchChatMessages, createHoverPrefetcher,
} from '../../services/inbox/chatCache';
import { loadWaBusinessMessages } from '../../services/inbox/chatMessageLoaders';

// Pages (shared with wa-personal)
import LabelsPage from '../global/LabelsPage';
import TagsKanbanPage from '../global/TagsKanbanPage';
import AgentsPage from '../global/AgentsPage';
import FollowUpsPage from '../global/FollowUpsPage';
import TemplatesPage from './TemplatesPage';

// Casa telefones tolerando código de país (55) e o 9º dígito BR. Canonicaliza
// para DDD + número (sem 55, sem o 9 do celular) e compara. Usado no deep-link
// da notificação de contato transferido (o telefone pode vir com/sem o 9).
const _digitsNoCC = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);  // tira código de país
  return d;
};
const _canonPhone = (raw) => {
  let d = _digitsNoCC(raw);
  if (d.length === 11 && d[2] === '9') d = d.slice(0, 2) + d.slice(3);  // tira o 9
  return d;
};
const _phoneMatches = (a, b) => {
  const da = _digitsNoCC(a);
  const db = _digitsNoCC(b);
  if (!da || !db) return false;
  if (da === db) return true;  // match exato (não colapsa o 9)
  // Só colapsa o 9º dígito quando os COMPRIMENTOS diferem (11 vs 10) — evita
  // casar um fixo de 10 díg com um celular diferente de mesmo sufixo.
  if (da.length !== db.length) return _canonPhone(a) === _canonPhone(b);
  return false;
};

const WABusinessInbox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme, colors } = useTheme();

  // Workspace delegado: a conexão pertence ao dono do workspace — nunca abrir
  // modal/popup de conexão aqui. Estável: delegação só muda com reload completo.
  const delegated = isDelegated();

  // SSE context
  const { isConnected: sseConnected, sseState, reconnectInfo, reconnect: reconnectSSE } = useSSE();

  // Bloco A3 (inbox): paridade da lista do Business com a do QR. Lido por ref
  // porque os handlers de SSE abaixo sao registrados uma vez so.
  const { flags: featureFlags } = useFeatureFlags();
  const paridadeBusinessRef = useRef(false);
  paridadeBusinessRef.current = Boolean(featureFlags?.inbox_business_list_parity);

  // Frente C1: com persistent_layout a NavSidebar mora no InboxLayout e este
  // shell so publica as props dela (ver efeito perto do render).
  const { layoutOwnsNav, setNavProps } = useInboxLayout();
  // Frente C4: cache de conversas abertas + prefetch no hover (decidido na
  // montagem, como o store da lista).
  const chatCacheOnRef = useRef(null);
  if (chatCacheOnRef.current === null) chatCacheOnRef.current = getBootFlag('chat_cache');
  const chatCacheOn = chatCacheOnRef.current;

  // Navigation state (igual ao pessoal)
  const [currentPage, setCurrentPage] = useState(location.state?.targetPage || 'inbox');

  useEffect(() => {
    if (location.state?.targetPage) {
      setCurrentPage(location.state.targetPage);
    }
    // Vindo de fora do shell (ex.: "Conexão WhatsApp" no inbox do Instagram):
    // o modal de conexão do canal mora aqui, então a intenção viaja por state.
    if (location.state?.openConnection && !isDelegated()) {
      setShowConnectionModal(true);
    }
    // Deep-link para uma conversa especifica (ex.: clicar numa notificacao de
    // handoff no painel do sino). Acha o chat por telefone/id e seleciona.
    // Tambem consome sessionStorage.pendingOpenChat (Kanban global e Painel do
    // Gestor entram no workspace de outro membro recarregando a pagina, e o
    // state se perde), como o shell do QR ja fazia (2026-09-15).
    let pendingOpenChat = null;
    try {
      const raw = sessionStorage.getItem('pendingOpenChat');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.contact_phone) {
          pendingOpenChat = parsed;
          sessionStorage.removeItem('pendingOpenChat');
        }
      }
    } catch (_e) { /* storage indisponivel */ }
    const openChat = location.state?.openChat || pendingOpenChat;
    if (openChat) {
      setCurrentPage('inbox');
      const phone = openChat.contact_phone;
      const findAndSelect = (list) => {
        const t = (list || []).find(
          (c) => (openChat.chat_id && c.id === openChat.chat_id)
            || _phoneMatches(c.contact_phone, phone)
        );
        if (t) { setSelectedChat(t); return true; }
        return false;
      };
      if (!findAndSelect(chatsRef.current)) {
        (async () => {
          // Usa a lista RETORNADA (chatsRef só atualiza no próximo commit).
          const reloaded = await loadChats(true);
          if (findAndSelect(reloaded)) return;
          // Fallback: busca server-side acha o contato fora da página 1 (ainda
          // é meu, só ranqueado abaixo do topo). Se vazio, saiu do workspace.
          try {
            const res = await waBusinessService.searchChats(phone);
            if (findAndSelect(res?.data || [])) return;
          } catch (_e) { /* ignore */ }
          setOwnershipToast({
            description: 'Este contato foi transferido e não está mais no seu inbox.',
          });
        })();
      }
    }
    if (location.state?.targetPage || location.state?.openChat || location.state?.openConnection) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, navigate]);

  // Chat state. Frente C2: com inbox_store a lista vive no inboxStore e
  // sobrevive a troca de inbox (a volta pinta o cache e revalida por baixo);
  // sem a flag e o useState de sempre, devolvido pelo mesmo hook.
  const listState = useChannelChats('wa_business');
  const { chats, setChats } = listState;
  const listStateRef = useRef(listState);
  listStateRef.current = listState;
  // Decisao de boot congelada na montagem (ver shell do QR).
  const listBootRef = useRef({ cacheUsable: listState.cacheUsable, stale: listState.stale });
  const [selectedChat, setSelectedChat] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    status: 'checking',
    phone_number: null,
    verified_name: null,
    token_expired: false 
  });
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  // "Reconectar" dispensado neste dispositivo (localStorage, por usuário): uma
  // vez fechado, NÃO reabre sozinho — só pelo menu (handleOpenConnection).
  // Mesma regra do inbox Evolution (paridade).
  const connectDismissedRef = useRef(false);
  const dismissConnect = useCallback(() => {
    connectDismissedRef.current = true;
    try { if (user?.id) localStorage.setItem(`infinitsales:wab_connect_dismissed:${user.id}`, '1'); } catch { /* ignore */ }
  }, [user?.id]);
  const clearConnectDismiss = useCallback(() => {
    connectDismissedRef.current = false;
    try { if (user?.id) localStorage.removeItem(`infinitsales:wab_connect_dismissed:${user.id}`); } catch { /* ignore */ }
  }, [user?.id]);
  useEffect(() => {
    try { connectDismissedRef.current = user?.id ? localStorage.getItem(`infinitsales:wab_connect_dismissed:${user.id}`) === '1' : false; } catch { /* ignore */ }
  }, [user?.id]);
  // Sem skeleton quando a lista ja esta no store (frente C2).
  const [loadingChats, setLoadingChats] = useState(!listBootRef.current.cacheUsable);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDisconnectPopup, setShowDisconnectPopup] = useState(true);
  const [showEmptyInboxModal, setShowEmptyInboxModal] = useState(false);
  const EMPTY_INBOX_KEY = 'infinitsales-empty-inbox-warned-v1';

  // Promo modal state (post-onboarding)
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoPlans, setPromoPlans] = useState([]);

  // Agent activation modal state (post-onboarding)
  const [showAgentActivation, setShowAgentActivation] = useState(false);
  const [activationLoading, setActivationLoading] = useState(false);

  // Toast de treinamento de agente REMOVIDO (aparecia constantemente p/ o
  // cliente). Mantemos só `trainingToastVisible` porque a notificação de criar
  // empresa depende dele.
  const {
    visible: trainingToastVisible,
  } = useAgentTrainingNotification();
  const [autoOpenAgentTrain, setAutoOpenAgentTrain] = useState(false);

  // Create-company notification — Business tier sem empresa
  const {
    visible: createCompanyVisible,
    dismiss: dismissCreateCompany,
    markCreated: markCompanyCreated,
  } = useCreateCompanyNotification();

  const handleCreateCompanyClick = useCallback(() => {
    markCompanyCreated();
    navigate('/account?tab=billing&open_company_form=1');
  }, [markCompanyCreated, navigate]);

  // Autopilot state
  const [autopilotContacts, setAutopilotContacts] = useState([]);
  const [autopilotMode, setAutopilotMode] = useState('off');
  const [blockedContacts, setBlockedContacts] = useState([]);
  const [activeAgentName, setActiveAgentName] = useState(null);

  // Handoff state: { [phone]: { state, state_metadata } }
  const [handoffStates, setHandoffStates] = useState({});
  // Chaves em handoff para a lista, memoizadas: `Object.keys` inline criava um
  // array novo a cada render e invalidava o itemData das linhas do ChatList.
  const handoffChatKeys = useMemo(() => Object.keys(handoffStates), [handoffStates]);

  // Toast queue for handoff transferred events
  const transferredNotifications = useContactTransferredNotifications();

  // Aviso de propriedade: contato deste workspace transferido para outro
  // responsável (motor de propriedade). { description } ou null.
  const [ownershipToast, setOwnershipToast] = useState(null);

  // Refs
  const selectedChatRef = useRef(selectedChat);
  const chatsRef = useRef(chats);
  const syncPollingRef = useRef(null);

  // Keep refs in sync
  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Modal "Nenhum contato exportado" — só para WA Business com inbox vazio,
  // mostra UMA vez (até o usuário clicar em "Ver tutorial").
  useEffect(() => {
    if (loadingChats) return;
    if (chats.length > 0) return;
    if (!connectionStatus.connected) return;
    let alreadyWarned = false;
    try { alreadyWarned = localStorage.getItem(EMPTY_INBOX_KEY) === '1'; } catch {}
    if (alreadyWarned) return;
    // Pequeno delay para evitar flash quando ainda está carregando dados
    const t = setTimeout(() => setShowEmptyInboxModal(true), 600);
    return () => clearTimeout(t);
  }, [loadingChats, chats.length, connectionStatus.connected]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup sync polling on unmount
  useEffect(() => {
    return () => {
      if (syncPollingRef.current) {
        clearInterval(syncPollingRef.current);
        syncPollingRef.current = null;
      }
    };
  }, []);

  // Post-onboarding: check for plan selection or show promo modal
  useEffect(() => {
    const justCompleted = localStorage.getItem('infinitsales-onboarding-just-completed');
    if (justCompleted !== 'true') return;

    localStorage.removeItem('infinitsales-onboarding-just-completed');

    const raw = localStorage.getItem('infinitsales-selected-plan');
    localStorage.removeItem('infinitsales-selected-plan');

    if (raw) {
      try {
        const { planId, timestamp } = JSON.parse(raw);
        const MAX_AGE_MS = 24 * 60 * 60 * 1000;
        if (planId && (Date.now() - timestamp) < MAX_AGE_MS) {
          (async () => {
            try {
              const result = await subscriptionService.getCheckoutUrl(planId);
              const checkoutUrl = result.data?.checkout_url || result.checkout_url;
              if (checkoutUrl) {
                window.location.href = checkoutUrl;
                return;
              }
            } catch (err) {
              console.error('[WABusinessInbox] Error getting checkout URL:', err);
            }
            loadPromoPlans();
          })();
          return;
        }
      } catch (e) {
        console.error('[WABusinessInbox] Error parsing selected plan:', e);
      }
    }

    loadPromoPlans();

    async function loadPromoPlans() {
      try {
        // Guard: only show promo modal for free users (tier=null)
        try {
          const status = await subscriptionService.getStatus();
          const tier = status?.data?.tier ?? status?.tier;
          if (tier) return;
        } catch (statusErr) {
          console.error('[WABusinessInbox] Error checking subscription status:', statusErr);
        }
        const res = await subscriptionService.getPlans();
        const data = res.data || res;
        const paid = Array.isArray(data) ? data.filter(p => !p.is_combo && p.price_brl > 0) : [];
        setPromoPlans(paid);
        setShowPromoModal(true);
      } catch (err) {
        console.error('[WABusinessInbox] Error loading plans for promo:', err);
      }
    }
  }, []);

  // Load autopilot config
  const loadAutopilotConfig = useCallback(async () => {
    try {
      const response = await authService.getAutopilotConfig();
      if (response?.data) {
        setAutopilotContacts(response.data.autopilot_contacts || []);
        setAutopilotMode(response.data.autopilot_mode || 'off');
        setBlockedContacts(response.data.blocked_contacts || []);
        setActiveAgentName(response.data.active_agent_name || null);
      }
    } catch (err) {
      console.error('[WA Business Inbox] Error loading autopilot config:', err);
    }
  }, []);

  // Carrega o autopilot-config quando o inbox está visível (cobre a montagem,
  // já que currentPage começa em 'inbox', E o retorno da tela de agentes).
  // Antes havia um 2º efeito "on mount" que duplicava a chamada na montagem.
  useEffect(() => {
    if (currentPage === 'inbox') {
      loadAutopilotConfig();
    }
  }, [currentPage, loadAutopilotConfig]);

  // Handler: toggle autopilot for a specific contact
  const handleToggleAutopilot = useCallback(async (phone, enabled) => {
    try {
      const response = await authService.toggleAutopilotContact(phone, enabled);
      if (response?.data?.autopilot_contacts) {
        setAutopilotContacts(response.data.autopilot_contacts);
      }
    } catch (err) {
      console.error('[WA Business Inbox] Error toggling autopilot:', err);
    }
  }, []);

  // Handler: toggle blocked contact (exclude from auto-respond when mode is "all")
  const handleToggleBlockedContact = useCallback(async (phone, blocked) => {
    try {
      const response = await authService.toggleBlockedContact(phone, blocked);
      if (response?.data?.blocked_contacts) {
        setBlockedContacts(response.data.blocked_contacts);
      }
    } catch (err) {
      console.error('[WA Business Inbox] Error toggling blocked contact:', err);
    }
  }, []);

  // ============================================
  // SSE EVENT HANDLERS (WA Business only)
  // ============================================

  // Handle NEW_MESSAGE from SSE
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    // Only process WA Business messages
    if (data?.source !== 'wa_business') return;
    
    console.log('[WA Business Inbox] SSE NEW_MESSAGE:', data?.chat_id);
    
    if (data && data.chat_id) {
      updateChatWithNewMessage(data);
    }
  }, []);

  // Handle MESSAGE_STATUS from SSE
  useSSEEvent(SSE_EVENTS.MESSAGE_STATUS, (data) => {
    // Only process WA Business status updates
    if (data?.source !== 'wa_business') return;
    
    console.log('[WA Business Inbox] SSE MESSAGE_STATUS:', data?.wamid);
    // Status updates are handled in ChatWindow
  }, []);

  // Handle CHAT_UPDATE from SSE
  useSSEEvent(SSE_EVENTS.CHAT_UPDATE, (data) => {
    // Only process WA Business chat updates
    if (data?.source !== 'wa_business') return;

    console.log('[WA Business Inbox] SSE CHAT_UPDATE');
    // Com a flag, uma rajada vira uma recarga so (espelha o QR).
    if (paridadeBusinessRef.current) scheduleChatsReload();
    else loadChats(true);
  }, []);

  // Handle SYNC_CHAT_ADDED from SSE (progressive sync after OAuth)
  useSSEEvent(SSE_EVENTS.SYNC_CHAT_ADDED, (data) => {
    if (data?.source !== 'wa_business') return;
    console.log('[WA Business Inbox] SSE SYNC_CHAT_ADDED:', data?.total_chats);
    // A coexistencia emite um evento POR conversa importada: sem debounce,
    // importar 5 mil conversas eram 5 mil recargas da lista.
    if (paridadeBusinessRef.current) scheduleChatsReload();
    else loadChats(true);
  }, []);

  // Handle SYNC_PROGRESS from SSE
  useSSEEvent(SSE_EVENTS.SYNC_PROGRESS, (data) => {
    if (data?.source !== 'wa_business') return;
    console.log('[WA Business Inbox] SSE SYNC_PROGRESS:', data?.meta_progress, '%');
  }, []);

  // Handle chat_state_changed from SSE (handoff detection)
  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    if (data?.source !== 'wa_business' || data?.type !== 'chat_state_changed') return;
    const phone = data.contact_phone;
    if (!phone) return;
    console.log('[WA Business Inbox] Chat state changed:', phone, data.state);
    if (data.state === 'handoff') {
      setHandoffStates(prev => ({
        ...prev,
        [phone]: { state: 'handoff', state_metadata: data.state_metadata || {} }
      }));
    } else {
      setHandoffStates(prev => {
        const next = { ...prev };
        delete next[phone];
        return next;
      });
    }
  }, []);

  // Handle CONTACT_TRANSFERRED — fired immediately when the agent transfers
  // (vs. chat_state_changed which only fires on the next inbound message).
  useSSEEvent(SSE_EVENTS.CONTACT_TRANSFERRED, (data) => {
    if (data?.source !== 'wa_business') return;
    const phone = data.contact_phone;
    if (!phone) return;
    console.log('[WA Business Inbox] Contact transferred:', phone, data.reason);
    // Update badge state immediately so ChatList shows "Transferido" without reload
    setHandoffStates(prev => ({
      ...prev,
      [phone]: { state: 'handoff', state_metadata: data.state_metadata || {} }
    }));
    // Enqueue toast
    transferredNotifications.enqueue(data);
  }, [transferredNotifications]);

  // Handle CONTACT_OWNERSHIP_CHANGED — motor de propriedade: o contato mudou
  // de dono entre workspaces. Quem perdeu a visão remove o chat da lista (e
  // fecha a conversa aberta, com aviso); quem recebeu recarrega a lista — o
  // cartão semeado aparece no próximo fetch. Contas sem company nunca recebem
  // este evento, então nada muda fora do fluxo de empresa.
  useSSEEvent(SSE_EVENTS.CONTACT_OWNERSHIP_CHANGED, (data) => {
    const myId = user?.id;
    if (!data?.phone || !myId) return;
    // Payload traz os canais afetados; este inbox só reage ao wa_business.
    if (Array.isArray(data.channels) && data.channels.length > 0
        && !data.channels.includes('wa_business')) return;
    const phoneDigits = String(data.phone).replace(/\D/g, '');
    if (!phoneDigits) return;
    const matchesPhone = (c) =>
      String(c?.contact_phone || '').replace(/\D/g, '') === phoneDigits;

    // hidden_for/visible_for cobrem multi-hop (transferência reversa): listam
    // TODAS as partições afetadas, não só prev/new/receiver do doc principal.
    // Fallback p/ o shape antigo quando o backend não envia as listas.
    const hiddenForMe = Array.isArray(data.hidden_for) && data.hidden_for.includes(myId);
    const visibleForMe = Array.isArray(data.visible_for) && data.visible_for.includes(myId);
    const lostLegacy = data.prev_owner === myId && data.new_owner !== myId;
    const receivedLegacy = data.receiver === myId || data.new_owner === myId;

    if (hiddenForMe || (lostLegacy && !visibleForMe)) {
      // Perdi a visão do contato: sai da lista e fecha o chat se era o aberto.
      console.log('[WA Business Inbox] Ownership perdido:', phoneDigits);
      setChats(prev => prev.filter(c => !matchesPhone(c)));
      setSelectedChat(prev => (prev && matchesPhone(prev) ? null : prev));
      const newOwnerName = data.assigned_to?.name;
      setOwnershipToast({
        description: newOwnerName
          ? `Novo responsável: ${newOwnerName}.`
          : 'Este contato agora é atendido por outro responsável.',
      });
    } else if (visibleForMe || receivedLegacy) {
      // Recebi/restaurei o contato: refaz o fetch p/ o cartão semeado aparecer.
      console.log('[WA Business Inbox] Ownership recebido:', phoneDigits);
      loadChats(false);
    }
  }, [user?.id]);

  // Agente ligado/desligado por outro membro do mesmo workspace: sincroniza o
  // estado do autopilot para o toggle refletir na hora em todas as telas.
  useSSEEvent(SSE_EVENTS.AUTOPILOT_CHANGED, (data) => {
    if (!data) return;
    if (Array.isArray(data.autopilot_contacts)) {
      setAutopilotContacts(data.autopilot_contacts);
    }
    if (data.autopilot_mode) {
      setAutopilotMode(data.autopilot_mode);
    }
  }, []);

  // Agente retomado por outro membro: limpa o handoff (banner + tag) na hora.
  useSSEEvent(SSE_EVENTS.AGENT_RESUMED, (data) => {
    const phone = data?.contact_phone;
    if (!phone) return;
    setHandoffStates(prev => {
      const next = { ...prev };
      delete next[phone];
      return next;
    });
  }, []);

  // Telefones cobertos pelo ultimo lote POST /chats/state-batch. Frente C4:
  // ao abrir uma conversa que o lote ja cobriu, o estado de handoff e o que o
  // shell guarda em handoffStates (mantido ao vivo pelo SSE), sem o GET
  // /chats/{fone}/state por clique.
  const batchCoveredRef = useRef(new Set());

  // Load handoff state when selecting a chat
  const checkChatHandoffState = useCallback(async (phone) => {
    if (!phone) return;
    if (chatCacheOn && batchCoveredRef.current.has(phone)) return;
    try {
      const result = await getChatState(phone);
      if (result?.state === 'handoff') {
        setHandoffStates(prev => ({
          ...prev,
          [phone]: { state: 'handoff', state_metadata: result.state_metadata || {} }
        }));
      } else {
        setHandoffStates(prev => {
          const next = { ...prev };
          delete next[phone];
          return next;
        });
      }
    } catch (err) {
      console.error('[WA Business Inbox] Error checking handoff state:', err);
    }
  }, [chatCacheOn]);

  // Handler: resume agent for a handoff chat
  const handleResumeAgent = useCallback(async (phone) => {
    try {
      await resumeAgent(phone);
      setHandoffStates(prev => {
        const next = { ...prev };
        delete next[phone];
        return next;
      });
    } catch (err) {
      console.error('[WA Business Inbox] Error resuming agent:', err);
    }
  }, []);

  // Pré-carrega o estado de handoff de todos os chats em UMA única request
  // batch. Sem isso a tag "Transferido" só aparece após clicar no chat ou
  // após uma nova mensagem disparar o SSE. Substitui o loop de N getChatState
  // que demorava ~2 minutos com lista grande.
  useEffect(() => {
    if (loadingChats || chats.length === 0) return;
    let cancelled = false;
    (async () => {
      const phones = chats
        .map(c => (c.contact_phone || '').trim())
        .filter(Boolean);
      if (phones.length === 0) return;
      try {
        const states = await getChatStatesBatch(phones);
        if (cancelled) return;
        batchCoveredRef.current = new Set(phones);
        const handoffMap = {};
        Object.entries(states).forEach(([phone, state]) => {
          if (state?.state === 'handoff') {
            handoffMap[phone] = {
              state: 'handoff',
              state_metadata: state.state_metadata || {},
            };
          }
        });
        if (Object.keys(handoffMap).length > 0) {
          setHandoffStates(prev => ({ ...prev, ...handoffMap }));
        }
      } catch (err) {
        console.warn('[WA Business Inbox] handoff batch fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [loadingChats, chats.length]);

  // Handle SYNC_COMPLETED from SSE (final reload after history sync)
  useSSEEvent(SSE_EVENTS.SYNC_COMPLETED, (data) => {
    if (data?.source !== 'wa_business') return;
    console.log('[WA Business Inbox] SSE SYNC_COMPLETED:', data?.chats_synced, 'chats');

    // Stop polling fallback if active
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
    }

    loadChats(true);
  }, []);

  // ============================================
  // DATA LOADING
  // ============================================

  const checkConnection = useCallback(async () => {
    try {
      const response = await waBusinessService.getStatus();
      const data = response || { connected: false, status: 'disconnected' };
      
      setConnectionStatus(data);
      
      if (data.token_expired) {
        console.warn('[WA Business Inbox] Token expired');
      }
      
      return data.connected;
    } catch (err) {
      console.error('[WA Business Inbox] Error checking connection:', err);
      setConnectionStatus({ connected: false, status: 'error' });
      return false;
    }
  }, []);

  // Pagination state — first page on load, "Carregar mais" appends.
  // 30 mantém o batch de handoff-state pequeno (1 round de paralelismo no
  // backend) e prioriza naturalmente os contatos mais recentes, já que a
  // resposta vem ordenada por last_message_at desc.
  const PAGE_SIZE = 30;
  // hasMore vive junto da lista (store ou useState, conforme a flag).
  const { hasMore: hasMoreChats, setHasMore: setHasMoreChats } = listState;
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  // Filtro de etiqueta/funil ativo (ids). Ref (não state) p/ não recriar
  // loadChats nem disparar o efeito de init; a ChatList notifica via
  // onLabelFilterChange e o fetch passa a respeitar o funil — paginado.
  const labelFilterRef = useRef([]);
  // Filtros extras (responsável/data/sem-resposta) — mesma estratégia do
  // labelFilterRef: ref p/ não recriar loadChats; a ChatList notifica via
  // onExtraFiltersChange e o fetch server-side passa a respeitar.
  const extraFilterRef = useRef(null);
  // Ha filtro server-side ligado agora? (mesma regra do inbox do QR)
  const filtrosAtivos = useCallback(() => {
    const e = extraFilterRef.current;
    return (labelFilterRef.current || []).length > 0
      || Boolean(e && (e.member_id || e.since || e.created_from || e.created_to || e.unanswered
        || (e.custom_fields || []).length > 0));
  }, []);

  // Busca DENTRO dos filtros (INBOX_SEARCH_WITHIN_FILTERS_ENABLED): termo da
  // lupa informado pela ChatList. So vai ao servidor (`q`) com filtro ativo;
  // sem filtro, a busca global da ChatList segue como antes.
  const searchRef = useRef({ q: '', searchMessages: false });
  const extraComBusca = useCallback(() => {
    const s = searchRef.current;
    if (!s.q || !filtrosAtivos()) return extraFilterRef.current;
    return { ...(extraFilterRef.current || {}), q: s.q, search_messages: s.searchMessages };
  }, [filtrosAtivos]);

  // Recarga agrupada por filtro: uma rajada de mensagens de conversas fora do
  // resultado filtrado vira UMA requisicao. Espelha o wa_personal e o padrao
  // do Instagram/Messenger.
  const reloadDebounceRef = useRef(null);
  // Inicio da rajada pendente: com a paridade ligada, uma rajada continua (a
  // importacao da coexistencia) recarrega pelo menos a cada 4 s, em vez de
  // adiar a recarga para sempre.
  const reloadPendingSinceRef = useRef(0);
  const scheduleChatsReload = useCallback(() => {
    if (reloadDebounceRef.current) clearTimeout(reloadDebounceRef.current);
    const agora = Date.now();
    if (!reloadPendingSinceRef.current) reloadPendingSinceRef.current = agora;
    const estourouEspera = paridadeBusinessRef.current
      && agora - reloadPendingSinceRef.current >= 4000;
    reloadDebounceRef.current = setTimeout(() => {
      reloadDebounceRef.current = null;
      reloadPendingSinceRef.current = 0;
      loadChatsRef.current?.(true);
    }, estourouEspera ? 0 : 1200);
  }, []);
  // Indirecao por ref: loadChats e definido abaixo e nao pode entrar nas deps
  // (recriaria o callback a cada render).
  const loadChatsRef = useRef(null);
  useEffect(() => () => {
    if (reloadDebounceRef.current) clearTimeout(reloadDebounceRef.current);
  }, []);

  const loadChats = useCallback(async (silent = false) => {
    if (!silent) setLoadingChats(true);
    try {
      const response = await waBusinessService.getChats(PAGE_SIZE, 0, labelFilterRef.current, extraComBusca());
      const newChats = response.data || [];

      // Sort by last_message_at (newest first)
      const sortedChats = [...newChats].sort((a, b) => {
        const dateA = new Date(a.last_message_at || a.updated_at || 0);
        const dateB = new Date(b.last_message_at || b.updated_at || 0);
        return dateB - dateA;
      });

      setChats(sortedChats);
      setHasMoreChats(Boolean(response.has_more));
      listStateRef.current.markFetched({
        page: 1,
        hasMore: Boolean(response.has_more),
        filters: { labelIds: labelFilterRef.current, extra: extraComBusca() },
      });
      return sortedChats;  // retorna a lista p/ o deep-link (chatsRef só atualiza
                           // no próximo commit).
    } catch (err) {
      console.error('[WA Business Inbox] Error loading chats:', err);
      if (!silent) setChats([]);
      return [];
    } finally {
      if (!silent) setLoadingChats(false);
    }
  }, [setChats, setHasMoreChats, extraComBusca]);
  // Publica o loadChats para o scheduleChatsReload (declarado antes dele).
  useEffect(() => { loadChatsRef.current = loadChats; }, [loadChats]);

  const loadMoreChats = useCallback(async () => {
    if (loadingMoreChats || !hasMoreChats) return;
    setLoadingMoreChats(true);
    try {
      const response = await waBusinessService.getChats(PAGE_SIZE, chats.length, labelFilterRef.current, extraComBusca());
      const more = response.data || [];
      setChats(prev => {
        const seen = new Set(prev.map(c => c.id));
        const merged = [...prev, ...more.filter(c => !seen.has(c.id))];
        merged.sort((a, b) => {
          const dateA = new Date(a.last_message_at || a.updated_at || 0);
          const dateB = new Date(b.last_message_at || b.updated_at || 0);
          return dateB - dateA;
        });
        return merged;
      });
      setHasMoreChats(Boolean(response.has_more));
      listStateRef.current.markFetched({
        page: (listStateRef.current.page || 1) + 1,
        hasMore: Boolean(response.has_more),
      });
    } catch (err) {
      console.error('[WA Business Inbox] Error loading more chats:', err);
    } finally {
      setLoadingMoreChats(false);
    }
  }, [chats.length, hasMoreChats, loadingMoreChats, setChats, setHasMoreChats, extraComBusca]);

  // Recebe a seleção de etiquetas da ChatList, guarda no ref e recarrega a
  // 1ª página server-side — assim o filtro por funil "puxa tudo" paginado,
  // não só o que já estava na tela. Lista vazia => sem skeleton perpétuo.
  const handleLabelFilterChange = useCallback((labelIds) => {
    labelFilterRef.current = Array.isArray(labelIds) ? labelIds.filter(Boolean) : [];
    loadChats(false);
  }, [loadChats]);

  // Filtros extras (responsável/data/sem-resposta) → refetch server-side.
  const handleExtraFilterChange = useCallback((extra) => {
    const hasAny = extra && (extra.member_id || extra.since || extra.created_from || extra.created_to || extra.unanswered
      || (extra.custom_fields || []).length > 0);
    extraFilterRef.current = hasAny ? extra : null;
    loadChats(false);
  }, [loadChats]);

  // Termo da lupa (debounced na ChatList). Com filtro ativo, muda a lista.
  const handleSearchTermChange = useCallback((q, searchMessages) => {
    const prev = searchRef.current;
    const next = { q: q || '', searchMessages: Boolean(searchMessages) };
    searchRef.current = next;
    if (filtrosAtivos() && (prev.q !== next.q || prev.searchMessages !== next.searchMessages)) {
      loadChats(false);
    }
  }, [filtrosAtivos, loadChats]);

  // ============================================
  // CHAT UPDATE LOGIC
  // ============================================

  const updateChatWithNewMessage = useCallback((data) => {
    const { chat_id, message, contact, chat_last_message_at } = data;

    if (!chat_id) return;

    // Server-side timestamp do replace_item do chat. Tem prioridade sobre o
    // message.timestamp (que vem do webhook Meta e pode estar defasado em
    // ate ~30s, quebrando a ordenacao por data em tempo real).
    const serverTs = chat_last_message_at
      || message?.timestamp
      || new Date().toISOString();

    // Decidido FORA do updater: o React pode invocar o updater mais de uma vez
    // (StrictMode) e disparar fetch la dentro gera requisicao duplicada — a
    // mesma armadilha ja documentada no Instagram. E com filtro ativo a lista
    // e o RESULTADO do filtro: quem decide se a conversa entra e o servidor.
    if (!(chatsRef.current || []).some(c => c.id === chat_id)) {
      if (data.hidden_for_owner === true) return;   // anti-ressurreicao
      scheduleChatsReload();
      return;
    }

    // Frente C4: conversa em cache mas NAO aberta recebe a mensagem como
    // patch (append com dedup), em vez de perder a entrada. A conversa aberta
    // e do ChatWindow, que espelha o proprio estado no cache.
    if (chatCacheOn && message && !message.from_me && selectedChatRef.current?.id !== chat_id) {
      appendChatMessage('wa_business', chat_id, message, (m) => m?.id || m?.wamid);
    }

    setChats(prevChats => {
      const existingIndex = prevChats.findIndex(c => c.id === chat_id);

      if (existingIndex >= 0) {
        const newChats = [...prevChats];
        const [existingChat] = newChats.splice(existingIndex, 1);

        const updatedChat = {
          ...existingChat,
          contact_name: existingChat.contact_name || contact?.name,
          // Card de ferramenta ou de falha do agente (tool_event) não é anexo:
          // mantém o preview anterior, como Instagram e Messenger fazem.
          // Antes a lista mostrava "Anexo tool_event" (Famiglia, 16/09).
          last_message_text: message?.text
            || (message?.type === 'tool_event'
              ? existingChat.last_message_text
              : `Anexo ${message?.type || 'midia'}`),
          last_message_at: serverTs,
          last_message_from_me: message?.from_me || false,
          unread_count: message?.from_me
            ? existingChat.unread_count
            : (existingChat.unread_count || 0) + 1,
        };

        // Re-sort para garantir que a ordem se mantenha quando varios chats
        // recebem mensagens em rajada (sem isso, [updated, ...rest] pode
        // ficar fora de ordem se `rest` ja estava stale).
        const merged = [updatedChat, ...newChats];
        merged.sort((a, b) => {
          const da = new Date(a.last_message_at || a.updated_at || 0);
          const db = new Date(b.last_message_at || b.updated_at || 0);
          return db - da;
        });
        return merged;
      }
      // Sem branch de "chat novo": o caso foi tratado antes do updater.
      return prevChats;
    });
  }, [scheduleChatsReload, chatCacheOn, setChats]);

  // ============================================
  // INITIAL LOAD
  // ============================================

  useEffect(() => {
    const init = async () => {
      // PARALELO (espelho do wa-personal): a lista não espera o /status —
      // fetch especulativo com erro engolido; modal/empty-state seguem
      // guiados pelo status.
      // Frente C2: lista ja no store pinta na hora; revalida em silencio se
      // tem mais de 15 s, e nem isso se ainda esta fresca.
      const boot = listBootRef.current;
      const chatsPromise = boot.cacheUsable && !boot.stale
        ? Promise.resolve()
        : loadChats(boot.cacheUsable).catch(() => {});
      const isConnected = await checkConnection();
      if (isConnected) {
        await chatsPromise;
      } else if (delegated) {
        // Workspace delegado desconectado: sem modal de conexão — o
        // empty-state (renderPageContent) cobre o aviso.
        await chatsPromise;
        setLoadingChats(false);
      } else {
        // Não reabre o modal se já foi dispensado neste dispositivo (reconectar
        // segue pelo menu).
        if (!connectDismissedRef.current) setShowConnectionModal(true);
        await chatsPromise;
        setLoadingChats(false);
      }
    };
    init();
  }, [checkConnection, loadChats, delegated]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleConnectionSuccess = async (accountData) => {
    console.log('[WA Business Inbox] Connection success:', accountData);
    setShowConnectionModal(false);
    clearConnectDismiss();

    setConnectionStatus({
      connected: true,
      status: 'connected',
      phone_number: accountData?.phone_number,
      verified_name: accountData?.verified_name,
      token_expired: false,
    });

    // Load chats immediately (may be empty while sync runs)
    await loadChats(false);

    // Polling fallback: reload chats periodically until sync completes
    // Stops on SYNC_COMPLETED event or after max attempts
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
    }
    let attempts = 0;
    const maxAttempts = 10;
    syncPollingRef.current = setInterval(async () => {
      attempts++;
      console.log(`[WA Business Inbox] Sync polling ${attempts}/${maxAttempts}`);
      await loadChats(true);
      if (attempts >= maxAttempts && syncPollingRef.current) {
        clearInterval(syncPollingRef.current);
        syncPollingRef.current = null;
      }
    }, 5000);
  };

  // Nova conversa pelo botão + da busca: get-or-create do chat no backend e abre.
  const handleStartNewChat = async (phone, name) => {
    // Conversa já carregada com este número (com ou sem 55 e 9º dígito): abre
    // ela, como no QR, antes de pedir o get-or-create ao backend.
    const existing = findChatByPhone(chatsRef.current, phone, (c) => c.contact_phone);
    if (existing) {
      if (selectedChat?.id !== existing.id) handleSelectChat(existing);
      return;
    }
    const chat = await waBusinessService.ensureChat(phone, name);
    if (!chat) return;
    setChats((prev) => (prev.some((c) => c.id === chat.id) ? prev : [chat, ...prev]));
    setSelectedChat(chat);
  };

  const handleSelectChat = (chat) => {
    // Toggle: clicar no chat ja selecionado faz deselecionar (limpa painel
    // de mensagens). Util para o operador limpar a selecao sem precisar
    // voltar via mobile back ou navegar.
    if (selectedChat && selectedChat.id === chat?.id) {
      setSelectedChat(null);
      return;
    }

    setSelectedChat(chat);

    setChats(prevChats =>
      prevChats.map(c =>
        c.id === chat.id ? { ...c, unread_count: 0 } : c
      )
    );

    if (isMobile) setShowSidebar(false);

    // Check handoff state for this chat
    checkChatHandoffState(chat.contact_phone);
  };

  const handleBackToChats = () => {
    setSelectedChat(null);
    setShowSidebar(true);
  };

  // Frente C4: passar o mouse na linha (desktop, debounce 150 ms) ja busca a
  // primeira pagina de mensagens para o cache; o clique pinta na hora.
  const hoverPrefetcher = useMemo(() => createHoverPrefetcher(150), []);
  useEffect(() => () => hoverPrefetcher.cancel(), [hoverPrefetcher]);
  const handleHoverChat = useCallback((chat) => {
    const id = chat?.id;
    if (!id || chat?._isNew) return;
    // A conversa aberta e do ChatWindow (que ja buscou): nao duplica o GET.
    if (selectedChatRef.current?.id === id) return;
    hoverPrefetcher.schedule(() => {
      prefetchChatMessages('wa_business', id, () => loadWaBusinessMessages(id));
    });
  }, [hoverPrefetcher]);
  // Posicao da lista guardada sem re-render (so leitura na proxima montagem).
  const handleListScroll = useCallback((offset) => {
    setInboxScrollTopSilent('wa_business', offset);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await checkConnection();
    if (connectionStatus.connected) {
      await loadChats(false);
    }
    setIsRefreshing(false);
  };

  const handleMessageSent = (messageData) => {
    if (selectedChat && messageData) {
      updateChatWithNewMessage({
        chat_id: selectedChat.id,
        message: {
          text: messageData.text,
          timestamp: new Date().toISOString(),
          from_me: true,
        },
      });
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Deseja desconectar sua conta WhatsApp Business?')) return;
    
    try {
      await waBusinessService.disconnect();
      handleDisconnected();
    } catch (err) {
      console.error('[WA Business Inbox] Error disconnecting:', err);
      alert('Erro ao desconectar. Tente novamente.');
    }
  };

  // Callback when disconnected (from modal or direct)
  const handleDisconnected = () => {
    console.log('[WA Business Inbox] Account disconnected');
    setConnectionStatus({ connected: false, status: 'disconnected' });
    setChats([]);
    setSelectedChat(null);
    if (!connectDismissedRef.current) setShowDisconnectPopup(true);
  };

  const handlePageChange = (page) => {
    if (page === 'meta-leads') {
      navigate('/meta-leads');
      return;
    }
    if (page === 'saved-messages') {
      navigate('/saved-messages');
      return;
    }
    if (page === 'campaigns') {
      navigate('/campaigns');
      return;
    }
    setCurrentPage(page);
  };
  const handleOpenConnection = () => {
    // Guarda defensiva: em workspace delegado não há ação de conectar.
    if (isDelegated()) return;
    setShowConnectionModal(true);
  };

  // ============================================
  // STYLES (Padronizado com WhatsApp Pessoal)
  // ============================================

  const styles = {
    container: {
      display: 'flex',
      width: '100%',
      // Mobile: 100dvh + overflow hidden + overscroll-behavior none segura a
      // tela SEM position:fixed (fixed + teclado do iOS travava a UI ao sair do
      // foco do chat). Inputs >=16px já evitam o zoom ao focar.
      ...(isMobile
        ? { height: '100dvh', overscrollBehavior: 'none' }
        : { height: '100vh', width: '100vw' }),
      // Frente C1: dentro do InboxLayout o shell e uma coluna flex ao lado da
      // NavSidebar do layout, nao a tela inteira.
      ...(layoutOwnsNav && !isMobile ? { width: 'auto', flex: 1, minWidth: 0 } : {}),
      overflow: 'hidden',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    mainArea: {
      flex: 1,
      display: 'flex', 
      overflow: 'hidden' 
    },
    inboxLayout: { 
      flex: 1, 
      display: 'flex', 
      overflow: 'hidden' 
    },
    sidebar: {
      width: isMobile ? '100%' : '395px',
      minWidth: isMobile ? '100%' : '395px',
      maxWidth: isMobile ? '100%' : '395px',
      // 100dvh acompanha a barra do navegador no mobile; reserva espaço p/ a
      // barra inferior (bottom tabs).
      height: isMobile ? '100dvh' : '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgPrimary,
      borderRight: isMobile ? 'none' : `1px solid ${colors.borderColor}`,
      position: 'relative',
      // Mobile: reserva espaço para a barra superior (54) e a inferior (58).
      paddingTop: isMobile ? 54 : 0,
      paddingBottom: isMobile ? 58 : 0,
    },
    inboxBrandBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 10px 0 16px',
      height: 58,
      flexShrink: 0,
      borderBottom: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgPrimary,
    },
    inboxBrand: {
      fontSize: 20,
      fontWeight: 700,
      letterSpacing: '0.2px',
      color: colors.textPrimary,
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
    },
    // Logo padronizada (igual ao login): container branco circular; sombra/borda
    // no tema claro (fundo da logo é branco e some no claro).
    inboxBrandLogo: {
      width: 32, height: 32, borderRadius: 8,
      backgroundColor: '#ffffff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', flexShrink: 0,
      boxShadow: theme === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.18)',
      border: theme === 'dark' ? 'none' : `1px solid ${colors.borderColor}`,
    },
    chatArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgChat,
      overflow: 'hidden',
      height: isMobile ? '100dvh' : undefined,
    },
    mobileHeader: {
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    mobileMenuBtn: {
      width: 40, 
      height: 40,
      border: 'none', 
      background: 'transparent',
      color: colors.textPrimary, 
      cursor: 'pointer',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
    },
    mobileTitle: {
      flex: 1, 
      fontWeight: 500, 
      fontSize: 18, 
      textAlign: 'center', 
      color: colors.textPrimary,
    },
    disconnectionPopup: {
      position: 'fixed', 
      bottom: 60, 
      left: '50%', 
      transform: 'translateX(-50%)',
      backgroundColor: colors.bgPrimary, 
      color: colors.textPrimary,
      padding: '12px 20px', 
      display: 'flex', 
      alignItems: 'center', 
      gap: 12,
      zIndex: 90, 
      borderRadius: 12, 
      border: `1px solid ${colors.danger}`, 
      boxShadow: colors.shadowLg,
    },
    disconnectionBtn: {
      padding: '6px 14px', 
      backgroundColor: colors.danger, 
      color: 'white',
      border: 'none', 
      borderRadius: 6, 
      cursor: 'pointer', 
      fontSize: 13, 
      fontWeight: 500,
    },
    tokenWarning: {
      position: 'fixed',
      bottom: 60,
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: `${colors.warning || '#f59e0b'}15`,
      border: `1px solid ${colors.warning || '#f59e0b'}40`,
      color: colors.textPrimary,
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      zIndex: 90,
      borderRadius: 12,
      boxShadow: colors.shadowLg,
    },
    reconnectBtn: {
      padding: '6px 14px', 
      backgroundColor: colors.accentPrimary, 
      color: 'white',
      border: 'none', 
      borderRadius: 6, 
      cursor: 'pointer', 
      fontSize: 13, 
      fontWeight: 500,
    },
    banner: {
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0,
      color: 'white', 
      padding: '8px 16px',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: 8,
      fontSize: 14, 
      zIndex: 100,
    },
  };

  // ============================================
  // RENDER
  // ============================================

  const renderPageContent = () => {
    switch (currentPage) {
      case 'followups': return <FollowUpsPage />;
      case 'templates': return <TemplatesPage />;
      case 'labels': return <LabelsPage />;
      case 'tags-kanban': return (
        <TagsKanbanPage
          onOpenChat={(contact) => {
            // Cartao de Instagram ou Messenger (chave opaca, sem telefone):
            // abre o inbox DO CANAL pelo mesmo deep-link do sino/toast
            // (state.openChat), em vez de cair no inbox de WhatsApp.
            const ref = String(contact?.contact_ref || contact?.phone || '');
            // Canal opaco (Instagram, Messenger, widget): rota e campo do openChat
            // pelo mapa de canais, a partir do prefixo da chave (2026-09-23).
            if (isOpaqueKey(ref) || isOpaqueChannel(contact?.channel)) {
              const canal = isOpaqueKey(ref) ? channelOf(ref) : contact.channel;
              navigate(routeOf(canal), { state: { openChat: openChatState(canal, ref) } });
              return;
            }
            const normalized = (contact?.phone || '').replace(/\D/g, '');
            // Igual exato primeiro, depois com e sem 55 e 9º dígito: o card
            // 553491609492 abre a conversa 5534991609492 em vez de duplicar.
            const match = findChatByPhone(chatsRef.current, normalized, (c) => c.contact_phone);
            if (match) {
              handleSelectChat(match);
            } else if (normalized) {
              // Contato sem chat previo (ex: importado via CSV). Cria
              // pseudo-chat client-side com _isNew=true. O ChatWindow do
              // Business detecta a flag, pula o fetch de mensagens e usa
              // sendMessageDirect (que internamente chama
              // get_or_create_chat no backend antes de enviar).
              const pseudo = {
                id: `new_${normalized}`,
                _isNew: true,
                account_id: null,
                user_id: null,
                contact_phone: normalized,
                contact_name: contact?.display_name || contact?.name || normalized,
                contact_profile_picture_url: contact?.profile_pic_url || null,
                last_message_text: null,
                last_message_at: null,
                last_message_from_me: null,
                last_inbound_at: null,
                unread_count: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
              setChats((prev) => {
                const exists = prev.some((c) =>
                  (c.contact_phone || '').replace(/\D/g, '') === normalized
                );
                return exists ? prev : [pseudo, ...prev];
              });
              handleSelectChat(pseudo);
            }
            setCurrentPage('inbox');
          }}
        />
      );
      case 'agents': return (
        <AgentsPage
          autoOpenTrainingFor={autoOpenAgentTrain}
          onAutoOpenConsumed={() => setAutoOpenAgentTrain(false)}
        />
      );
      case 'inbox':
      default:
        // Workspace delegado sem WhatsApp conectado: sem lista/conversa nem
        // ação de conectar — só o aviso com o caminho de volta aos workspaces.
        if (delegated && !connectionStatus.connected && connectionStatus.status !== 'checking') {
          return <DelegatedDisconnectedState workspaceName={getDelegation()?.targetName} />;
        }
        return (
          <div style={styles.inboxLayout}>
            {/* Mobile estilo WhatsApp: LISTA em tela cheia ou CONVERSA em tela
                cheia, nunca as duas (sem overlay/transform). Desktop: lado a lado. */}
            {(!isMobile || !selectedChat) && (
              <aside style={styles.sidebar}>
                {!isMobile && (
                  <div style={styles.inboxBrandBar}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <span style={styles.inboxBrandLogo}>
                        <img
                          src={process.env.PUBLIC_URL + '/logo.jpeg'}
                          alt="InfinitSales"
                          style={{ width: 20, height: 20, objectFit: 'contain' }}
                        />
                      </span>
                      <span style={styles.inboxBrand}>InfinitSales</span>
                    </div>
                    <NotificationBell />
                  </div>
                )}
                <ChatList
                  chats={chats}
                  selectedChat={selectedChat}
                  onSelectChat={handleSelectChat}
                  onStartNewChat={handleStartNewChat}
                  loading={loadingChats}
                  hasMore={hasMoreChats}
                  onLoadMore={loadMoreChats}
                  loadingMore={loadingMoreChats}
                  autopilotContacts={autopilotContacts}
                  autopilotMode={autopilotMode}
                  activeAgentName={activeAgentName}
                  handoffChats={handoffChatKeys}
                  onLabelFilterChange={handleLabelFilterChange}
                  onExtraFiltersChange={handleExtraFilterChange}
                  onSearchTermChange={handleSearchTermChange}
                  // Frentes C2 e C4: sem as flags, undefined e 0 (como hoje).
                  onHoverChat={chatCacheOn ? handleHoverChat : undefined}
                  initialScrollOffset={listState.fromStore ? listState.scrollTop : 0}
                  onScrollOffset={listState.fromStore ? handleListScroll : undefined}
                />
              </aside>
            )}

            {(!isMobile || selectedChat) && (
            <main style={styles.chatArea}>
              <ChatWindow
                chat={selectedChat}
                chats={chats}
                isMobile={isMobile}
                onBack={handleBackToChats}
                onMessageSent={handleMessageSent}
                autopilotContacts={autopilotContacts}
                autopilotMode={autopilotMode}
                blockedContacts={blockedContacts}
                activeAgentName={activeAgentName}
                onToggleAutopilot={handleToggleAutopilot}
                onToggleBlockedContact={handleToggleBlockedContact}
                isHandoff={selectedChat ? !!handoffStates[selectedChat.contact_phone] : false}
                handoffMetadata={selectedChat ? handoffStates[selectedChat.contact_phone]?.state_metadata : null}
                onResumeAgent={handleResumeAgent}
                onChatPromoted={({ tempId, realChatId, phone }) => {
                  // ChatWindow chama isso apos a 1a mensagem com sucesso em
                  // pseudo-chat. Trocamos o id pseudo pelo real na lista e
                  // no selectedChat para que SSE/markAsRead/getMessages
                  // passem a funcionar normalmente.
                  setChats((prev) => prev.map((c) =>
                    c.id === tempId
                      ? { ...c, id: realChatId, _isNew: false }
                      : c
                  ));
                  setSelectedChat((curr) =>
                    curr && curr.id === tempId
                      ? { ...curr, id: realChatId, _isNew: false }
                      : curr
                  );
                }}
                onContactNameUpdated={({ chatId, newName }) => {
                  // Sincroniza nome editado no painel "Dados do contato" com a
                  // ChatList lateral e com o header (selectedChat).
                  setChats((prev) => prev.map((c) =>
                    c.id === chatId ? { ...c, contact_name: newName } : c
                  ));
                  setSelectedChat((curr) =>
                    curr && curr.id === chatId
                      ? { ...curr, contact_name: newName }
                      : curr
                  );
                }}
              />
            </main>
            )}
          </div>
        );
    }
  };

  // Frente C1: a NavSidebar do layout recebe as props deste shell. Sem deps
  // de proposito: os handlers sao recriados a cada render e o layout so
  // re-renderiza quando currentPage/isRefreshing/mobileHideNav mudam.
  const navProps = {
    currentPage,
    onPageChange: handlePageChange,
    onRefresh: handleRefresh,
    onOpenConnection: handleOpenConnection,
    isRefreshing,
    mobileHideNav: isMobile && currentPage === 'inbox' && !!selectedChat,
  };
  useEffect(() => {
    if (layoutOwnsNav) setNavProps(navProps);
  });

  return (
    <div style={styles.container}>
      {/* SSE Failed Banner */}
      {sseState === SSE_STATES.FAILED && (
        <div
          style={{ ...styles.banner, backgroundColor: '#ef4444', cursor: 'pointer' }}
          onClick={reconnectSSE}
        >
          <WifiOff size={16} />
          <span>Conexão perdida. Clique para reconectar.</span>
        </div>
      )}

      {/* Reconnecting Banner */}
      {reconnectInfo && (
        <div style={{ ...styles.banner, backgroundColor: colors.warning || '#f59e0b' }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Reconectando... ({reconnectInfo.attempt}/{reconnectInfo.maxAttempts})</span>
        </div>
      )}

      {/* Navigation Sidebar (no InboxLayout quando persistent_layout esta ON) */}
      {!layoutOwnsNav && (
        <NavSidebar
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRefresh={handleRefresh}
          onOpenConnection={handleOpenConnection}
          isRefreshing={isRefreshing}
          mobileHideNav={isMobile && currentPage === 'inbox' && !!selectedChat}
        />
      )}

      {/* Main Area */}
      <div style={styles.mainArea}>
        {renderPageContent()}
      </div>

      {/* Connection Modal (OAuth - específico do Business) */}
      <ConnectionModal
        isOpen={showConnectionModal}
        onClose={() => { setShowConnectionModal(false); dismissConnect(); }}
        onConnected={handleConnectionSuccess}
        connectionStatus={connectionStatus}
        onDisconnected={handleDisconnected}
      />

      {/* Token Expired Warning (específico do Business) */}
      {!delegated && connectionStatus.connected && connectionStatus.token_expired && (
        <div style={styles.tokenWarning}>
          <AlertCircle size={18} color={colors.warning || '#f59e0b'} />
          <span style={{ fontSize: 14 }}>
            Token expirado. Reconecte sua conta.
          </span>
          <button
            style={styles.reconnectBtn}
            onClick={() => setShowConnectionModal(true)}
          >
            Reconectar
          </button>
        </div>
      )}

      {/* Disconnection Popup */}
      {!delegated &&
        currentPage === 'inbox' &&
        !connectionStatus.connected &&
        connectionStatus.status !== 'checking' &&
        !showConnectionModal &&
        showDisconnectPopup && (
        <div style={styles.disconnectionPopup}>
          <WifiOff size={18} style={{ color: colors.danger }} />
          <span style={{ fontSize: 14, color: colors.textSecondary }}>WhatsApp desconectado</span>
          <button style={styles.disconnectionBtn} onClick={() => setShowConnectionModal(true)}>
            Reconectar
          </button>
          <button
            style={{ padding: 4, background: 'transparent', border: 'none', color: colors.textTertiary, cursor: 'pointer' }}
            onClick={() => { setShowDisconnectPopup(false); dismissConnect(); }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Empty Inbox Modal — primeira vez que WA Business abre sem chats */}
      <EmptyInboxModal
        open={showEmptyInboxModal}
        onClose={() => {
          try { localStorage.setItem(EMPTY_INBOX_KEY, '1'); } catch {}
          setShowEmptyInboxModal(false);
        }}
        onSeeTutorial={() => {
          navigate('/tutorials#wa-business-connect');
        }}
        onReload={() => {
          window.location.reload();
        }}
      />

      {/* Post-onboarding Promo Modal */}
      <PromoModal
        isOpen={showPromoModal}
        onClose={() => {
          setShowPromoModal(false);
          const activationShown = localStorage.getItem('infinitsales-agent-activation-shown');
          if (!activationShown && user?.active_agent_id) {
            setShowAgentActivation(true);
            localStorage.setItem('infinitsales-agent-activation-shown', 'true');
          }
        }}
        plans={promoPlans}
      />

      {/* Toast de treinamento de agente removido (aparecia constantemente). */}

      {/* Create-company Notification — só aparece após o toast de treino sumir */}
      <CreateCompanyNotification
        isOpen={createCompanyVisible && !trainingToastVisible && currentPage === 'inbox' && !showPromoModal && !showAgentActivation}
        onCreate={handleCreateCompanyClick}
        onDismiss={dismissCreateCompany}
      />

      {/* Contact Transferred Notification (toast, top-right) — agregado via
          useContactTransferredNotifications. Quando N transferencias chegam
          em janela de 8s, mostra UMA notif "N atendimentos transferidos". */}
      <ContactTransferredNotification
        isOpen={!!transferredNotifications.current && !showPromoModal && !showAgentActivation}
        batch={transferredNotifications.current?.batch || []}
        onOpenChat={(payload) => {
          if (!payload) return;
          const phone = payload.contact_phone;
          setCurrentPage('inbox');
          const match = (c) => (payload.chat_id && c.id === payload.chat_id)
            || _phoneMatches(c.contact_phone, phone);
          const target = (chatsRef.current || []).find(match);
          if (target) {
            setSelectedChat(target);
          } else {
            (async () => {
              const reloaded = await loadChats(true);
              let hit = (reloaded || []).find(match);
              if (!hit) {
                try {
                  const res = await waBusinessService.searchChats(phone);
                  hit = (res?.data || []).find(match);
                } catch (_e) { /* ignore */ }
              }
              if (hit) setSelectedChat(hit);
              else setOwnershipToast({
                description: 'Este contato foi transferido e não está mais no seu inbox.',
              });
            })();
          }
          transferredNotifications.dismissCurrent();
        }}
        onDismiss={transferredNotifications.dismissCurrent}
      />

      {/* Aviso de propriedade — contato deste workspace transferido para
          outro responsável (o chat sai da lista na hora). */}
      <SwipeToast
        isOpen={!!ownershipToast && !showPromoModal && !showAgentActivation}
        icon={<UserCheck size={20} />}
        title="Contato transferido"
        description={ownershipToast?.description}
        onDismiss={() => setOwnershipToast(null)}
        accentColor="#2563EB"
      />

      {/* Post-onboarding Agent Activation Modal */}
      <AgentActivationModal
        isOpen={showAgentActivation}
        loading={activationLoading}
        onClose={() => setShowAgentActivation(false)}
        onActivateAll={async () => {
          setActivationLoading(true);
          try {
            await authService.updateAutopilotConfig('all');
            setAutopilotMode('all');
          } catch (err) {
            console.error('Error activating agent for all:', err);
          } finally {
            setActivationLoading(false);
            setShowAgentActivation(false);
          }
        }}
        onSetSchedule={() => {
          setShowAgentActivation(false);
          setCurrentPage('agents');
        }}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { overflow: hidden; }
      `}</style>
    </div>
  );
};

export default WABusinessInbox;